import { isNumber, isUndefined } from 'lodash-es';

import type { MinMax } from '../../core/types/min-max.js';
import { isBandScale } from '../../core/utils/detect-scale.js';
import type { RectanglePoint } from '../components/overlay/explore/types.js';
import { isRectangleSelected } from '../components/overlay/utils/is-rectangle-selected.js';
import { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  RectDatapointInternal,
  RectSeriesWithDatapoints,
  type AxisIdToScaleDomain,
  type BarSeriesWithDatapoints,
  type XYChartVariantsInternal,
} from '../types/xy-chart-internals.js';
import { XYAccessorDataTypes, type BarDatapoint } from '../types/xy-chart.js';
import { getCurrentScale } from '../utils/get-scale.js';
import { toAbsolute } from '../utils/value-representation.js';

export interface XYChartSelectionRectangleMetadata {
  series: RectSeriesWithDatapoints | BarSeriesWithDatapoints;
  datapoint: RectDatapointInternal | BarDatapoint;
}

const toNumber = (value: number | Date) =>
  typeof value === 'number' ? value : value.getTime();

const parseValue = (value: XYAccessorDataTypes) => {
  if (typeof value === 'number' || typeof value === 'string') {
    return value;
  }
  return value.getTime();
};

const hashColumnKey = (x0: XYAccessorDataTypes, x1?: XYAccessorDataTypes) =>
  x1 && x0 < x1
    ? `${parseValue(x0)}_${parseValue(x1 ?? x0)}`
    : `${parseValue(x1 ?? x0)}_${parseValue(x0)}`;

const sortRangeKeys = (keys: string[]) =>
  keys.toSorted((a, b) => {
    const [a0] = a.split('_');
    const [b0] = b.split('_');
    if (!isNaN(parseInt(a0)) && !isNaN(parseInt(b0))) {
      return parseInt(a0) - parseInt(b0);
    }
    return 0;
  });

export const isValidNumeric = (v?: XYAccessorDataTypes) =>
  v !== undefined && typeof v !== 'string';

export interface RectangleBoundaries<T extends XYAccessorDataTypes> {
  [id: string]: {
    min: T;
    max: T;
  };
}

export interface RectAxisSelectConfig {
  uniqueColumns: Set<string>;
  uniqueRows: Set<string>;
  columnsMap: Map<string, XYChartSelectionRectangleMetadata[]>;
  boundaries: RectangleBoundaries<XYAccessorDataTypes>;
}

const applySerialization = (
  range: string[],
  columnsMap: Map<string, XYChartSelectionRectangleMetadata[]>,
  callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
  rectsByRange: RectanglePoint[],
) => {
  for (const columnKey of range) {
    const rectsInColumn = columnsMap.get(columnKey);
    if (rectsInColumn) {
      for (const rect of rectsInColumn) {
        const mapped = callback(rect);
        if (mapped) {
          rectsByRange.push(mapped);
        }
      }
    }
  }
};

export const useSortRectangleDatapoints = (
  seriesVariantsProps: XYChartVariantsInternal[],
) => {
  const rectAxisSelectConfig = new Map<string, RectAxisSelectConfig>();

  function updateBoundariesForAxis<T extends XYAccessorDataTypes>(
    boundaries: RectangleBoundaries<XYAccessorDataTypes>,
    axisName: string,
    v0: T,
    v1?: T,
  ) {
    if (typeof v0 === 'string' || typeof v1 === 'string') {
      return;
    }
    if (isValidNumeric(v0) && isValidNumeric(v1)) {
      const v0Num = toNumber(v0);
      const v1Num = toNumber(v1);
      if (
        boundaries[axisName] &&
        isNumber(boundaries[axisName].min) &&
        isNumber(boundaries[axisName].max)
      ) {
        boundaries[axisName].min = Math.min(
          toNumber(boundaries[axisName].min),
          v0Num,
          v1Num,
        );
        boundaries[axisName].max = Math.max(
          toNumber(boundaries[axisName].max),
          v0Num,
          v1Num,
        );
      } else {
        boundaries[axisName] = {
          min: Math.min(v0Num, v1Num),
          max: Math.max(v0Num, v1Num),
        };
      }
    }
  }

  const handleAddValue = (rect: XYChartSelectionRectangleMetadata) => {
    const { x0, x1, y0, y1 } = rect.datapoint;
    const xAxisId = rect.series.xAxisId;
    const yAxisId = rect.series.yAxisId;

    if (
      isUndefined(x0) &&
      isUndefined(x1) &&
      isUndefined(y0) &&
      isUndefined(y1)
    ) {
      return;
    }

    const isVerticalBar =
      typeof x0 === 'string' || (typeof y0 !== 'string' && !isUndefined(x0));
    const isHorizontalBar =
      typeof y0 === 'string' ||
      (typeof x0 !== 'string' && isUndefined(x0) && !isUndefined(y0));

    const primaryAxisId = isVerticalBar
      ? xAxisId
      : isHorizontalBar
      ? yAxisId
      : xAxisId;
    const secondaryAxisId = isVerticalBar
      ? yAxisId
      : isHorizontalBar
      ? xAxisId
      : yAxisId;
    const primaryV0 = isVerticalBar ? x0 : isHorizontalBar ? y0 : x0;
    const primaryV1 = isVerticalBar ? x1 : isHorizontalBar ? y1 : x1;
    const secondaryV0 = isVerticalBar ? y0 : isHorizontalBar ? x0 : y0;
    const secondaryV1 = isVerticalBar ? y1 : isHorizontalBar ? x1 : y1;

    if (!rectAxisSelectConfig.has(primaryAxisId)) {
      rectAxisSelectConfig.set(primaryAxisId, {
        uniqueColumns: new Set<string>(),
        uniqueRows: new Set<string>(),
        columnsMap: new Map<string, XYChartSelectionRectangleMetadata[]>(),
        boundaries: {},
      });
    }
    const axisConfig = rectAxisSelectConfig.get(primaryAxisId) ?? {
      uniqueColumns: new Set<string>(),
      uniqueRows: new Set<string>(),
      columnsMap: new Map<string, XYChartSelectionRectangleMetadata[]>(),
      boundaries: {},
    };

    let hashedKey;
    if (typeof primaryV0 === 'string' || typeof primaryV1 === 'string') {
      hashedKey = primaryV0 as string;
    } else if (primaryV0 !== undefined) {
      hashedKey = hashColumnKey(primaryV0, primaryV1);
      if (secondaryV0 !== undefined) {
        updateBoundariesForAxis(
          axisConfig.boundaries,
          secondaryAxisId,
          secondaryV0,
          secondaryV1,
        );
        const rowKey = hashColumnKey(secondaryV0, secondaryV1);
        axisConfig.uniqueRows.add(rowKey);
      }
    }
    if (isValidNumeric(primaryV0) && primaryV0 !== undefined) {
      updateBoundariesForAxis(
        axisConfig.boundaries,
        primaryAxisId,
        primaryV0,
        primaryV1,
      );
    }
    if (!hashedKey) {
      return;
    }

    if (!axisConfig.uniqueColumns.has(hashedKey)) {
      axisConfig.uniqueColumns.add(hashedKey);
      axisConfig.columnsMap.set(hashedKey, []);
    }

    const rectsInRange = axisConfig.columnsMap.get(hashedKey) || [];
    rectsInRange.push(rect);
    axisConfig.columnsMap.set(hashedKey, rectsInRange);
  };

  const getAxisSortedValues = (axisId: string) => {
    const sortedData = rectAxisSelectConfig.get(axisId);
    if (sortedData) {
      const { uniqueColumns, uniqueRows, columnsMap } = sortedData;

      const sortedColumns = sortRangeKeys(Array.from(uniqueColumns));
      const sortedRows = sortRangeKeys(Array.from(uniqueRows));

      return {
        sortedColumns,
        columnsMap,
        sortedRows,
      };
    }
    return undefined;
  };

  const getAxisValues = (axisId: string) => rectAxisSelectConfig.get(axisId);

  const handleSearchRange = (
    start: XYAccessorDataTypes,
    end: XYAccessorDataTypes,
    sortedColumns: string[],
  ) => {
    const sX = start instanceof Date ? start.getTime() : Number(start);
    const eX = end instanceof Date ? end.getTime() : Number(end);
    const [min, max] = sX <= eX ? [sX, eX] : [eX, sX];
    return sortedColumns.filter((key) => {
      const [x0Str, x1Str] = key.split('_');
      const colX0 = Number(x0Str);
      const colX1 = Number(x1Str);
      const isInRange = isRectangleSelected({
        x: {
          rectangleStart: colX0,
          rectangleEnd: colX1,
          selectionStart: min,
          selectionEnd: max,
        },
      });
      return isInRange;
    });
  };

  const handleGetRectanglesByNumericalRangeForHorizontalBar = (
    start: number,
    end: number,
    axisConfig: RectAxisSelectConfig,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
  ) => {
    const rectsByRange: RectanglePoint[] = [];
    const [selectionStart, selectionEnd] =
      start < end ? [start, end] : [end, start];

    for (const rectsInColumn of axisConfig.columnsMap.values()) {
      for (const rect of rectsInColumn) {
        const rectanglePoint = callback(rect);
        if (!rectanglePoint) {
          continue;
        }

        const barStart = rectanglePoint.x;
        const barEnd = rectanglePoint.x + rectanglePoint.width;
        const isInRange = isRectangleSelected({
          x: {
            rectangleStart: barStart,
            rectangleEnd: barEnd,
            selectionStart,
            selectionEnd,
          },
        });
        if (isInRange) {
          rectsByRange.push(rectanglePoint);
        }
      }
    }

    return rectsByRange;
  };

  const handleGetRectanglesByNumericalRangeForVerticalBar = (
    start: XYAccessorDataTypes,
    end: XYAccessorDataTypes,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
    axisId: string,
  ) => {
    const sortedValuesByAxis = getAxisSortedValues(axisId);
    const rectsByRange: RectanglePoint[] = [];

    if (sortedValuesByAxis) {
      const { sortedColumns, columnsMap } = sortedValuesByAxis;
      const range = handleSearchRange(start, end, sortedColumns);
      applySerialization(range, columnsMap, callback, rectsByRange);
    }

    return rectsByRange;
  };

  const handleGetRectanglesByCategoricalRange = (
    start: number,
    end: number,
    axisId: string,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
  ) => {
    const categoricalValues = getAxisValues(axisId);
    const rectsByRange: RectanglePoint[] = [];
    if (categoricalValues) {
      const { columnsMap } = categoricalValues;
      const [selectionStart, selectionEnd] =
        start < end ? [start, end] : [end, start];

      for (const [, rectsInColumn] of columnsMap) {
        for (const rect of rectsInColumn) {
          const rectanglePoint = callback(rect);
          const barStart = rectanglePoint.x;
          const barEnd = rectanglePoint.x + rectanglePoint.width;
          const isInRange = isRectangleSelected({
            x: {
              rectangleStart: barStart,
              rectangleEnd: barEnd,
              selectionStart,
              selectionEnd,
            },
          });
          if (isInRange) {
            rectsByRange.push(rectanglePoint);
          }
        }
      }
    }

    return rectsByRange;
  };

  const isHorizontalBarConfiguration = (
    axisConfig: RectAxisSelectConfig,
  ): boolean => {
    if (axisConfig.columnsMap.size === 0) {
      return false;
    }

    const firstColumn = Array.from(axisConfig.columnsMap.values())[0];
    const firstDatapoint = firstColumn?.[0]?.datapoint;

    return firstDatapoint && isUndefined(firstDatapoint.x0);
  };

  const searchAllAxisDatapoints = (
    start: number,
    end: number,
    callback: (point: XYChartSelectionRectangleMetadata) => RectanglePoint,
    axesIds: string | string[],
    axisIdToScales: AxisIdToScales,
    axisIdToScaleDomain: AxisIdToScaleDomain,
    axisIDToIsRelative: Map<string, boolean>,
  ) => {
    const axesCurrentSelection: Record<string, RectanglePoint[]> = {};
    const highlightedDatapoints: RectanglePoint[] = [];

    if (!axisIdToScales) {
      return { axesCurrentSelection, highlightedDatapoints };
    }

    const axesIdsArray = typeof axesIds === 'string' ? [axesIds] : axesIds;

    axesIdsArray.forEach((axisId) => {
      if (rectAxisSelectConfig.has(axisId)) {
        const currentScale = getCurrentScale(axisIdToScales, axisId);
        if (isBandScale(currentScale)) {
          const rectsByRange = handleGetRectanglesByCategoricalRange(
            start,
            end,
            axisId,
            callback,
          );

          for (const rect of rectsByRange) {
            highlightedDatapoints.push(rect);
          }
        } else {
          const axisConfig = rectAxisSelectConfig.get(axisId);

          if (axisConfig && isHorizontalBarConfiguration(axisConfig)) {
            const horizontalBarResults =
              handleGetRectanglesByNumericalRangeForHorizontalBar(
                start,
                end,
                axisConfig,
                callback,
              );
            highlightedDatapoints.push(...horizontalBarResults);

            return;
          }

          let startDomain = currentScale.invert(start);
          let endDomain = currentScale.invert(Math.ceil(end));
          if (axisIDToIsRelative.get(axisId)) {
            const domain = axisIdToScaleDomain.get(axisId) as MinMax;
            startDomain = toAbsolute(startDomain as number, domain);
            endDomain = toAbsolute(endDomain as number, domain);
          }
          const seriesHighlightedDatapoints =
            handleGetRectanglesByNumericalRangeForVerticalBar(
              startDomain,
              endDomain,
              callback,
              axisId,
            );

          for (const datapoint of seriesHighlightedDatapoints) {
            highlightedDatapoints.push(datapoint);
          }
          axesCurrentSelection[axisId] = seriesHighlightedDatapoints;
        }
      }
    });

    return { axesCurrentSelection, highlightedDatapoints };
  };

  return {
    addValue: handleAddValue,
    getCurrentSelectConfig: () => rectAxisSelectConfig,
    searchAllAxisDatapoints,
  };
};
